import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Map as MapIcon, Save, Trash2, UserPlus, Users } from "lucide-react";
import { territoriesApi, type TerritoryDto } from "@/api/territories";
import { usersApi } from "@/api/users";
import { Badge, Button, Label } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useDialogs } from "@/hooks/useDialogs";

const ALL_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA",
  "WV","WI","WY",
];

export function TerritoriesPage() {
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const list = useQuery({ queryKey: ["territories"], queryFn: () => territoriesApi.list() });
  const users = useQuery({
    queryKey: ["users-all"],
    queryFn: () => usersApi.list({ pageSize: 100 }).then(r => r.data),
  });

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["territories"] });

  const create = useMutation({
    mutationFn: (name: string) =>
      territoriesApi.create({ name, code: name.slice(0, 4).toUpperCase(), isActive: true }),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Territories"
        description="Geographic routing: assign US states to territories and liaisons to territories."
        actions={
          <Button size="sm" onClick={async () => {
            const name = await dialogs.prompt({
              title: "New territory",
              label: "Territory name",
              placeholder: "e.g. Mountain West",
              confirmLabel: "Save",
            });
            if (name) create.mutate(name);
          }}>
            <MapIcon size={14} strokeWidth={1.5} aria-hidden /> New territory
          </Button>
        }
      />

      {/* Self-capped: a single-column stack of label-left / control-right
          rows, which stretches badly rather than absorbing width. See
          `AppShell.tsx`. */}
      <div className="grid gap-space-3 max-w-5xl">
        {list.data?.map((t) => (
          <TerritoryCard
            key={t.id}
            territory={t}
            expanded={expandedId === t.id}
            onExpand={() => setExpandedId(prev => prev === t.id ? null : t.id)}
            users={users.data?.map(u => ({ id: u.id, name: u.displayName })) ?? []}
            onChanged={invalidate}
          />
        ))}
        {list.data?.length === 0 && (
          <div className="text-caption text-gray-mid italic">No territories yet.</div>
        )}
      </div>
    </>
  );
}

interface TerritoryCardProps {
  territory: TerritoryDto;
  expanded: boolean;
  onExpand: () => void;
  users: { id: string; name: string }[];
  onChanged: () => void;
}

function TerritoryCard({ territory, expanded, onExpand, users, onChanged }: TerritoryCardProps) {
  const assignedStates = useMemo(
    () => new Set(territory.states.map(s => s.stateCode)),
    [territory.states],
  );
  const [selected, setSelected] = useState<Set<string>>(assignedStates);

  const save = useMutation({
    mutationFn: () => territoriesApi.assignStates(territory.id, Array.from(selected)),
    onSuccess: onChanged,
  });

  const toggleState = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelected(next);
  };

  return (
    <div className="border border-border-light rounded-md bg-white-pure">
      <button
        type="button"
        onClick={onExpand}
        className="w-full flex items-center justify-between p-space-3 text-left"
      >
        <div className="flex items-center gap-space-3">
          <Badge status={territory.isActive ? "gold" : "archived"}>{territory.code}</Badge>
          <div>
            <div className="font-semibold text-small">{territory.name}</div>
            <div className="text-caption text-gray-mid">
              {territory.states.length} states · {territory.liaisons.length} liaison{territory.liaisons.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <span className="text-caption text-gray-mid">{expanded ? "Collapse" : "Expand"}</span>
      </button>

      {expanded && (
        <div className="border-t border-border-light p-space-4 grid gap-space-4">
          {/* State assignment */}
          <div>
            <Label className="mb-space-2 block">States</Label>
            <div className="flex flex-wrap gap-space-1">
              {ALL_STATES.map(code => (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleState(code)}
                  className={
                    "px-space-2 py-[4px] text-caption font-mono rounded-sm border " +
                    (selected.has(code)
                      ? "border-gold bg-gold text-onyx font-semibold"
                      : "border-border-light bg-white-pure text-gray-mid hover:border-gold-shadow")
                  }
                >
                  {code}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-space-2">
              <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                <Save size={14} strokeWidth={1.5} aria-hidden />
                {save.isPending ? "Saving…" : "Save states"}
              </Button>
            </div>
          </div>

          {/* Liaisons */}
          <div>
            <Label className="mb-space-2 block">Liaisons</Label>
            <LiaisonList territory={territory} users={users} onChanged={onChanged} />
          </div>
        </div>
      )}
    </div>
  );
}

function LiaisonList({
  territory, users, onChanged,
}: { territory: TerritoryDto; users: { id: string; name: string }[]; onChanged: () => void }) {
  const [userId, setUserId] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => territoriesApi.assignLiaison(territory.id, userId, isPrimary),
    onSuccess: () => { setUserId(""); setIsPrimary(false); setError(null); onChanged(); },
    onError: (e: Error) => setError(e.message ?? "Assign failed."),
  });

  const remove = useMutation({
    mutationFn: (liaisonId: string) => territoriesApi.removeLiaison(territory.id, liaisonId),
    onSuccess: () => { setError(null); onChanged(); },
    onError: (e: Error) => setError(e.message ?? "Remove failed."),
  });

  const nameOf = (uid: string) => users.find(u => u.id === uid)?.name ?? uid.slice(0, 8);

  return (
    <div className="grid gap-space-2">
      {territory.liaisons.length > 0 && (
        <ul className="grid gap-space-1">
          {territory.liaisons.map(l => (
            <li key={l.id} className="flex items-center justify-between border border-border-light rounded-md px-space-3 py-space-2">
              <div className="flex items-center gap-space-2">
                <Users size={14} strokeWidth={1.5} aria-hidden className="text-gray-mid" />
                <span className="text-small">{nameOf(l.userId)}</span>
                {l.isPrimary && <Badge status="gold">Primary</Badge>}
                <span className="text-caption text-gray-mid">weight {l.routingWeight}</span>
              </div>
              <Button size="sm" variant="text" onClick={() => remove.mutate(l.id)}>
                <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-space-2 border-t border-border-light pt-space-3">
        <div className="flex-1 min-w-[12rem]">
          <Label className="block">Add liaison</Label>
          <select
            className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">Select user…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-space-2 text-caption">
          <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="accent-gold" />
          Primary
        </label>
        <Button size="sm" onClick={() => add.mutate()} disabled={!userId || add.isPending}>
          <UserPlus size={14} strokeWidth={1.5} aria-hidden /> Assign
        </Button>
      </div>

      {error && (
        <div className="text-small text-status-error" role="alert">{error}</div>
      )}
    </div>
  );
}
